import React, { useEffect } from 'react'
import { Field } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import FormMonthPicker from '../atoms/FormMonthPicker'
import FormSelect from '../atoms/FormSelect'
import { FormFieldLabel } from '../atoms/FormTextInput'
import { referenceSelectOptions } from './promotionReferenceOptions'
import { YesNoSegment } from './YesNoSegment'
import { FETCH_MOBILE_REFERENCE_VALUES } from 'src/redux/mobileReferenceReducer'
import { RootReducer } from 'src/redux/rootReducer'

const fieldClass = 'self-stretch flex flex-col justify-start items-start gap-2'

type LabelKey =
  | 'my_vehicles.promotion_metallic'
  | 'my_vehicles.promotion_drive_type'
  | 'my_vehicles.promotion_gear_box'
  | 'my_vehicles.promotion_damage'
  | 'my_vehicles.promotion_abs'
  | 'my_vehicles.promotion_heated_grips'
  | 'my_vehicles.promotion_box'
  | 'my_vehicles.promotion_windshield'
  | 'my_vehicles.promotion_service_history'
  | 'single_offer.motorbike_next_hu'
  | 'my_vehicles.promotion_section_equipment'
  | 'my_vehicles.promotion_section_service'

interface SelectField {
  name: 'drive_type' | 'gear_box'
  label: LabelKey
}

const selectFields: SelectField[] = [
  { name: 'drive_type', label: 'my_vehicles.promotion_drive_type' },
  { name: 'gear_box', label: 'my_vehicles.promotion_gear_box' }
]

interface EquipmentField {
  name: string
  label: LabelKey
}

const equipmentFields: EquipmentField[] = [
  { name: 'metallic', label: 'my_vehicles.promotion_metallic' },
  { name: 'damage_unrepaired', label: 'my_vehicles.promotion_damage' },
  { name: 'abs', label: 'my_vehicles.promotion_abs' },
  { name: 'heated_grips', label: 'my_vehicles.promotion_heated_grips' },
  { name: 'box', label: 'my_vehicles.promotion_box' },
  { name: 'windshield', label: 'my_vehicles.promotion_windshield' }
]

const textValue = (value: unknown): string => (typeof value === 'string' ? value : '')

const triStateValue = (value: unknown): boolean | null => {
  if (value === true || value === false) {
    return value
  }
  return null
}

const SectionTitle = ({ label }: { label: string }): JSX.Element => {
  return <div className="col-span-2 pt-2 text-zinc-900 text-base font-semibold font-Montserrat leading-6">{label}</div>
}

const VehicleEquipmentFields = (): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const driveTypes = useSelector((state: RootReducer) => state.mobileReferenceReducer.driveTypes)
  const gearBoxes = useSelector((state: RootReducer) => state.mobileReferenceReducer.gearBoxes)

  useEffect(() => {
    dispatch(FETCH_MOBILE_REFERENCE_VALUES())
  }, [dispatch])

  return (
    <>
      {selectFields.map((field) => (
        <Field key={field.name} name={field.name}>
          {({ input }) => {
            const value = textValue(input.value)
            const values = field.name === 'drive_type' ? driveTypes : gearBoxes
            return (
              <div className={fieldClass}>
                <FormFieldLabel label={t(field.label)} />
                <FormSelect
                  value={value}
                  onChange={input.onChange}
                  options={referenceSelectOptions({ values, labelForCode: (code) => code, currentCode: value })}
                  placeholder={t('my_vehicles.choose')}
                />
              </div>
            )
          }}
        </Field>
      ))}
      <SectionTitle label={t('my_vehicles.promotion_section_equipment')} />
      {equipmentFields.map((field) => (
        <Field key={field.name} name={field.name}>
          {({ input }) => (
            <div className={fieldClass}>
              <FormFieldLabel label={t(field.label)} />
              <YesNoSegment value={triStateValue(input.value)} onChange={input.onChange} />
            </div>
          )}
        </Field>
      ))}
      <SectionTitle label={t('my_vehicles.promotion_section_service')} />
      <Field name="next_hu">
        {({ input }) => (
          <div className={fieldClass}>
            <FormFieldLabel label={t('single_offer.motorbike_next_hu')} />
            <FormMonthPicker
              value={textValue(input.value)}
              onChange={input.onChange}
              placeholder={t('my_vehicles.promotion_month_placeholder')}
            />
          </div>
        )}
      </Field>
      <Field name="full_service_history">
        {({ input }) => (
          <div className={fieldClass}>
            <FormFieldLabel label={t('my_vehicles.promotion_service_history')} />
            <YesNoSegment value={triStateValue(input.value)} onChange={input.onChange} />
          </div>
        )}
      </Field>
    </>
  )
}

export default VehicleEquipmentFields
